import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, 
  CreditCard, 
  ShoppingBag, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Eye,
  DollarSign,
  Clock,
  Building2,
  Tag,
  Settings,
  Trash2,
  Download,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

// Confirmation Modal
function ConfirmModal({ open, title, message, onConfirm, onCancel, destructive }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="confirm-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${destructive ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <AlertTriangle className={`w-5 h-5 ${destructive ? 'text-red-600' : 'text-yellow-600'}`} />
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
        </div>
        <p className="text-slate-600 text-sm mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel} className="rounded-xl" data-testid="confirm-cancel-btn">
            Annuler
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            className="rounded-xl gap-2"
            data-testid="confirm-action-btn"
          >
            <Trash2 className="w-4 h-4" />
            Confirmer
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingSigles, setPendingSigles] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, destructive: false });

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(t('errors.adminRequired'));
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, siglesRes, ordersRes, profilesRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/pending-sigles`, { headers }),
        fetch(`${API}/admin/orders`, { headers }),
        fetch(`${API}/admin/profiles`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || data);
      }
      if (siglesRes.ok) setPendingSigles(await siglesRes.json());
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const ordersList = data.orders || data;
        setAllOrders(ordersList);
        setPendingOrders(ordersList.filter(o => o.status === 'pending' || o.status === 'pending_manual_payment'));
      }
      if (profilesRes.ok) setProfiles(await profilesRes.json());
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidateSigle = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/validate-sigle`, {
        method: 'PATCH', headers
      });
      if (response.ok) {
        toast.success(t('admin.sigleValidated'));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.operationFailed'));
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  const handleRejectSigle = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reject-sigle`, {
        method: 'PATCH', headers
      });
      if (response.ok) {
        toast.success(t('admin.sigleRejected'));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.operationFailed'));
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  const handleMarkOrderPaid = async (orderId) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}/status?status=paid`, {
        method: 'PATCH', headers
      });
      if (response.ok) {
        toast.success(t('admin.orderMarkedPaid'));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.operationFailed'));
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  const handleToggleProfile = async (profileId) => {
    try {
      const response = await fetch(`${API}/admin/profiles/${profileId}/toggle`, {
        method: 'PATCH', headers
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(profiles.map(p =>
          p.profile_id === profileId ? { ...p, is_active: data.is_active } : p
        ));
        toast.success(data.is_active ? t('admin.profileActivated') : t('admin.profileDeactivated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.operationFailed'));
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  // ---- DELETE HANDLERS ----

  const confirmDeleteUser = (u) => {
    setConfirmModal({
      open: true, destructive: true,
      title: 'Supprimer le compte',
      message: `Supprimer définitivement le compte "${u.email}" ainsi que tous ses profils, cartes et commandes associés ? Cette action est irréversible.`,
      onConfirm: () => executeDeleteUser(u.user_id)
    });
  };

  const executeDeleteUser = async (userId) => {
    setConfirmModal(prev => ({ ...prev, open: false }));
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE', headers
      });
      if (response.ok) {
        toast.success('Compte supprimé');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  const confirmDeleteProfile = (p) => {
    setConfirmModal({
      open: true, destructive: true,
      title: 'Supprimer le profil',
      message: `Supprimer définitivement le profil "${p.profile_id}" (${p.first_name || ''} ${p.last_name || ''}) et sa carte associée ? Cette action est irréversible.`,
      onConfirm: () => executeDeleteProfile(p.profile_id)
    });
  };

  const executeDeleteProfile = async (profileId) => {
    setConfirmModal(prev => ({ ...prev, open: false }));
    try {
      const response = await fetch(`${API}/admin/profiles/${profileId}`, {
        method: 'DELETE', headers
      });
      if (response.ok) {
        toast.success('Profil supprimé');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  const confirmDeleteSigle = (u) => {
    setConfirmModal({
      open: true, destructive: true,
      title: 'Supprimer le sigle',
      message: `Supprimer le sigle BUSINESS "${u.business_sigle}" de l'utilisateur "${u.email}" ? Les profils existants ne seront pas affectés.`,
      onConfirm: () => executeDeleteSigle(u.user_id)
    });
  };

  const executeDeleteSigle = async (userId) => {
    setConfirmModal(prev => ({ ...prev, open: false }));
    try {
      const response = await fetch(`${API}/admin/users/${userId}/sigle`, {
        method: 'DELETE', headers
      });
      if (response.ok) {
        toast.success('Sigle supprimé');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch { toast.error(t('errors.serverError')); }
  };

  // ---- DESIGN DOWNLOAD ----

  const handleDownloadDesign = async (fileId) => {
    try {
      const response = await fetch(`${API}/admin/design-files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (!response.ok) {
        toast.error('Fichier non trouvé');
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition');
      const filename = disposition?.match(/filename="?(.+)"?/)?.[1] || `design_${fileId}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur de téléchargement');
    }
  };

  // ---- FILTERS ----

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.business_sigle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProfiles = profiles.filter(p =>
    p.profile_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.owner_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Confirm Modal */}
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          destructive={confirmModal.destructive}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        />

        {/* Header */}
        <motion.div {...fadeUp} className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {t('admin.dashboard')}
              </h1>
              <p className="text-slate-600">
                {t('admin.manageEverything')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/admin/coupons')} className="gap-2" data-testid="coupons-btn">
                <Tag className="w-4 h-4" />
                Coupons
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/settings')} className="gap-2" data-testid="settings-btn">
                <Settings className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-admin-btn" disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {t('common.refresh')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            <motion.div {...fadeUp} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="stat-users">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#0A2540]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#0A2540]" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
              <p className="text-xs text-slate-500">{t('admin.totalUsers')}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 shadow-soft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_profiles}</p>
              <p className="text-xs text-slate-500">{t('admin.totalProfiles')}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-5 shadow-soft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#00C48C]/10 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#00C48C]" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.active_profiles}</p>
              <p className="text-xs text-slate-500">{t('admin.activeProfiles')}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="bg-white rounded-2xl p-5 shadow-soft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.paid_profiles}</p>
              <p className="text-xs text-slate-500">{t('admin.paidProfiles')}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.4 }} className="bg-white rounded-2xl p-5 shadow-soft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.pending_sigles}</p>
              <p className="text-xs text-slate-500">{t('admin.pendingSigles')}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.5 }} className="bg-white rounded-2xl p-5 shadow-soft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.pending_orders}</p>
              <p className="text-xs text-slate-500">{t('admin.pendingOrders')}</p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['overview', 'users', 'profiles', 'orders'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              className="rounded-xl whitespace-nowrap"
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
            >
              {t(`admin.tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
            </Button>
          ))}
        </div>

        {/* Search */}
        {activeTab !== 'overview' && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-xl"
                placeholder={t('admin.searchPlaceholder')}
                data-testid="admin-search"
              />
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {pendingSigles.length > 0 && (
              <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    {t('admin.pendingSigleValidation')} ({pendingSigles.length})
                  </h2>
                </div>
                <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
                  {pendingSigles.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                      <div>
                        <p className="font-medium text-slate-900">{u.email}</p>
                        <p className="text-lg font-bold text-yellow-700">{u.business_sigle}</p>
                        <p className="text-xs text-slate-500">{u.first_name} {u.last_name}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-[#00C48C] hover:bg-[#00A67E] rounded-lg gap-1" onClick={() => handleValidateSigle(u.user_id)} data-testid={`validate-${u.user_id}`}>
                          <CheckCircle className="w-4 h-4" />
                          {t('admin.validate')}
                        </Button>
                        <Button size="sm" variant="destructive" className="rounded-lg gap-1" onClick={() => handleRejectSigle(u.user_id)} data-testid={`reject-${u.user_id}`}>
                          <XCircle className="w-4 h-4" />
                          {t('admin.reject')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {pendingOrders.length > 0 && (
              <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-orange-600" />
                    {t('admin.pendingPayments')} ({pendingOrders.length})
                  </h2>
                </div>
                <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
                  {pendingOrders.map((order) => (
                    <div key={order.order_id} className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{order.order_id}</p>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            order.payment_mode === 'manual' || order.status === 'pending_manual_payment'
                              ? 'bg-amber-100 text-amber-700' : 'bg-[#0A2540]/10 text-[#0A2540]'
                          }`}>
                            {order.payment_mode === 'manual' || order.status === 'pending_manual_payment' ? 'Manuel' : 'Stripe'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{order.quantity}x {order.product_type} - ${order.total}</p>
                        <p className="text-xs text-slate-500">{order.user_email || order.user_id} &bull; {new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <Button size="sm" className="bg-[#00C48C] hover:bg-[#00A67E] rounded-lg gap-1" onClick={() => handleMarkOrderPaid(order.order_id)} data-testid={`pay-${order.order_id}`}>
                        <CheckCircle className="w-4 h-4" />
                        {t('admin.markPaid')}
                      </Button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {pendingSigles.length === 0 && pendingOrders.length === 0 && (
              <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{t('admin.allCaughtUp')}</h3>
                <p className="text-slate-600">{t('admin.noPendingActions')}</p>
              </motion.div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
            <div className="p-6 border-b border-slate-100">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('admin.allUsers')} ({filteredUsers.length})
              </h2>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.email')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.type')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.sigle')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.profiles')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.created')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((u) => {
                    const userProfiles = profiles.filter(p => p.user_id === u.user_id);
                    const isAdmin = u.role === 'admin' || u.account_type === 'admin';
                    return (
                      <tr key={u.user_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-slate-900">{u.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{u.first_name} {u.last_name}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            u.account_type === 'business' ? 'bg-purple-100 text-purple-700'
                            : isAdmin ? 'bg-red-100 text-red-700'
                            : 'bg-[#0A2540]/10 text-[#0A2540]'
                          }`}>{u.account_type}</span>
                        </td>
                        <td className="px-6 py-4">
                          {u.business_sigle ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">{u.business_sigle}</span>
                              {u.sigle_validated ? (
                                <CheckCircle className="w-4 h-4 text-[#00C48C]" />
                              ) : (
                                <Clock className="w-4 h-4 text-yellow-600" />
                              )}
                              <button
                                onClick={() => confirmDeleteSigle(u)}
                                className="text-red-400 hover:text-red-600 transition-colors"
                                title="Supprimer le sigle"
                                data-testid={`delete-sigle-${u.user_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{userProfiles.length}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          {!isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => confirmDeleteUser(u)}
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Profiles Tab */}
        {activeTab === 'profiles' && (
          <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
            <div className="p-6 border-b border-slate-100">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('admin.allProfiles')} ({filteredProfiles.length})
              </h2>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.profileId')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.owner')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.subscription')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProfiles.map((p) => (
                    <tr key={p.profile_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-bold text-[#0A2540]">{p.profile_id}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">{p.first_name} {p.last_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{p.owner_email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          p.subscription_active ? 'bg-[#00C48C]/10 text-[#00A67E]' : 'bg-red-100 text-red-700'
                        }`}>
                          {p.subscription_active ? t('admin.active') : t('admin.expired')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          p.is_active ? 'bg-[#0A2540]/10 text-[#0A2540]' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {p.is_active ? t('admin.visible') : t('admin.hidden')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <a href={`/user/${p.profile_id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="rounded-lg">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            variant={p.is_active ? 'outline' : 'default'}
                            className="rounded-lg"
                            onClick={() => handleToggleProfile(p.profile_id)}
                            data-testid={`toggle-admin-${p.profile_id}`}
                          >
                            {p.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => confirmDeleteProfile(p)}
                            data-testid={`delete-profile-${p.profile_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <motion.div {...fadeUp} className="bg-white rounded-2xl shadow-soft">
            <div className="p-6 border-b border-slate-100">
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                {t('admin.allOrders')} ({allOrders.length})
              </h2>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.orderId')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.product')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.quantity')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.total')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allOrders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-mono text-sm">{order.order_id}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{order.user_email || order.user_id}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">{order.product_type}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{order.quantity}</td>
                      <td className="px-6 py-4 font-medium">${order.total}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.payment_mode === 'manual' || order.status === 'pending_manual_payment'
                            ? 'bg-amber-100 text-amber-700' : 'bg-[#0A2540]/10 text-[#0A2540]'
                        }`}>
                          {order.payment_mode === 'manual' || order.status === 'pending_manual_payment' ? 'Manuel' : 'Stripe'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'paid' ? 'bg-[#00C48C]/10 text-[#00A67E]'
                          : order.status === 'pending' || order.status === 'pending_manual_payment'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                        }`}>
                          {order.status === 'pending_manual_payment' ? 'En attente' : order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 items-center">
                          {(order.status === 'pending' || order.status === 'pending_manual_payment') && (
                            <Button size="sm" className="bg-[#00C48C] hover:bg-[#00A67E] rounded-lg gap-1" onClick={() => handleMarkOrderPaid(order.order_id)} data-testid={`mark-paid-${order.order_id}`}>
                              <CheckCircle className="w-4 h-4" />
                              Valider
                            </Button>
                          )}
                          {order.status === 'paid' && !order.design_file_id && (
                            <span className="text-xs text-[#00C48C] flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Payé
                            </span>
                          )}
                          {order.design_file_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-lg gap-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              onClick={() => handleDownloadDesign(order.design_file_id)}
                              data-testid={`download-design-${order.order_id}`}
                            >
                              <Download className="w-4 h-4" />
                              Design
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {allOrders.length === 0 && (
                <div className="p-12 text-center text-slate-500">Aucune commande</div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
